import React from 'react';
import { Box, Button, Container, Stack, Typography, Card, CardContent, Chip, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '../../hooks/colorModeProvider';

import HomeStats from './homeStats';

import BowlscoutLogoDark from '../../resources/assets/images/bowlscout_logo_text_next.png';
import BowlscoutLogoLight from '../../resources/assets/images/bowlscout_logo_text_next_white.png';

import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GridViewIcon from '@mui/icons-material/GridView';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import GroupsIcon from '@mui/icons-material/Groups';

const FEATURES = [
  {
    icon: ScoreboardIcon,
    title: 'Live scoring',
    text: 'Score every end, set or skin in real time — with manual-total entry when you need it.'
  },
  {
    icon: AutoFixHighIcon,
    title: 'Automatic draws',
    text: 'Generate games and assign greens & rinks across every round. No more spreadsheets.'
  },
  {
    icon: GridViewIcon,
    title: 'Green & rink management',
    text: 'Drag-and-drop games across the field, with smart swap suggestions that keep play fair.'
  },
  {
    icon: LiveTvIcon,
    title: 'Broadcast-ready scoreboards',
    text: 'Push live scores to TVs and the web with shareable links and QR codes.'
  },
  {
    icon: LeaderboardIcon,
    title: 'Leaderboards & brackets',
    text: 'Automatic standings and knockout brackets that update the moment a game finishes.'
  },
  {
    icon: GroupsIcon,
    title: 'Players & quick games',
    text: 'Manage players by BSA number and spin up a quick game in seconds — no account needed.'
  }
];

const Home = () => {
  const navigate = useNavigate();
  const { mode } = useColorMode();
  const logo = mode === 'dark' ? BowlscoutLogoLight : BowlscoutLogoDark;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* Nav */}
      <Box
        component="header"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}>
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 1.5 }}>
            <img src={logo} alt="Bowlscout" style={{ height: 34, width: 'auto' }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="text" color="inherit" onClick={() => navigate('/signin')}>
                Sign In
              </Button>
              <Button variant="contained" color="primary" onClick={() => navigate('/signup')}>
                Get Started
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background:
            mode === 'dark'
              ? 'radial-gradient(60% 80% at 50% 0%, rgba(36,216,118,0.16), transparent 70%)'
              : 'radial-gradient(60% 80% at 50% 0%, rgba(76,175,80,0.12), transparent 70%)'
        }}>
        <Container maxWidth="md">
          <Stack alignItems="center" textAlign="center" sx={{ py: { xs: 8, md: 12 } }} gap={3}>
            <Typography variant="h2" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
              Everything your bowls club needs to run game day
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, fontWeight: 400 }}>
              Live scoring, automatic draws, green & rink management and broadcast-ready
              scoreboards — all in one place.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/signup')}>
                Get Started
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/signin')}>
                Sign In
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <HomeStats />

      {/* Features */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" textAlign="center" gap={1} sx={{ mb: 6 }}>
            <Typography variant="h4" fontWeight={800}>
              Built for bowls
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
              From sectional draws to live club scoreboards, Bowlscout covers the whole event.
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <Grid key={feature.title} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Chip
                        icon={<Icon />}
                        label={feature.title}
                        sx={{
                          mb: 1.5,
                          bgcolor: 'success.main',
                          color: '#09090b',
                          fontWeight: 700,
                          '& .MuiChip-icon': { color: '#09090b' }
                        }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        {feature.text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
        <Container maxWidth="md">
          <Stack alignItems="center" textAlign="center" gap={2}>
            <Typography variant="h4" fontWeight={800}>
              Ready to run your next event?
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 520, opacity: 0.85 }}>
              Create an account and be scoring your first game in minutes.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                mt: 1,
                bgcolor: 'background.paper',
                color: 'text.primary',
                '&:hover': { bgcolor: 'background.default' }
              }}>
              Create Free Account
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.default', mt: 'auto' }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 3 }}>
            <img src={logo} alt="Bowlscout" style={{ height: 24, width: 'auto' }} />
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Bowlscout. All rights reserved.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
